<template>
  <v-card color="card" class="elevation-2 rounded-lg">
    <v-sheet color="primary">
      <v-card-title class="pa-2">
        <div class="text-h6 ml-2 white--text">
          {{ getLabel('recruiter_permissions') }}
        </div>
      </v-card-title>
    </v-sheet>
    <v-card-text class="px-0">
      <v-form ref="form">
        <v-row class="mx-4">
          <v-select
            v-model="recruiterPermissions"
            class="mb-8 mt-6"
            :items="permissions"
            :label="getLabel('recruiter_permissions')"
            clearable
            multiple
            hide-details
          />
        </v-row>
        <v-row class="mx-4">
          <v-checkbox
            :input-value="hasAllPermissions"
            :label="getLabel('recruiter_all_permissions')"
            class="text-black"
            @change="toggleRecruiterPermissions"
          />
        </v-row>
      </v-form>
      <v-divider />
    </v-card-text>
    <v-card-actions class="pr-4 pb-5 pt-1">
      <v-spacer />
      <v-btn
        color="warning"
        dark
        @click="reset()"
      >
        <v-icon class="mr-2">mdi-broom</v-icon>
        {{ getLabel("clean") }}
      </v-btn>
      <v-btn
        class="ml-4"

        color="green darken-1"
        dark
        @click="savePermissions()"
      >
        <v-icon class="mr-2">mdi-content-save</v-icon>
        {{ getLabel("save") }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import { ref, computed } from '@vue/composition-api';
import { useMutation } from '@vue/apollo-composable';
import { useRoute } from 'vue2-helpers/vue-router';

import { getGraphqlSchema } from '@/graphql/index';
import { useMainStore } from '@/store/index';
import { getLabel, EventBus } from '@/utils/index';

export default {
  setup () {
    const store = useMainStore();

    const route = useRoute();
    const permissions = [];

    // TODO: add getReference to first request
    const permissionsReferences = {
      VIEW_JOB: 'Voir Besoin',
      CREATE_JOB: 'Créer Un Besoin',
      EDIT_JOB: 'Edit Besoin',
      DELETE_JOB: 'Supprimer Besoin',
      VIEW_OFFER: "Voir L'Offre",
      GENERATE_OFFER: 'Générer Une Offre',
      EDIT_OFFER: "Éditer L'Offre",
      DELETE_OFFER: "Supprimer L'Offre",
      DIFFUSE_OFFER: "Diffuser L'Offre",
      VIEW_APPLICATION: 'Voir La Candidature',
      EDIT_APPLICATION: 'Modifier La Candidature',
      CREATE_APPLICATION: 'Créer Une Candidature',
      VIEW_PROSPECT: 'Voir Le Prospect',
      CREATE_PROSPECT: 'Créer Un Prospect',
      EDIT_PROSPECT: 'Éditer Le Prospect',
      VIEW_VIVIER_PROSPECT: 'Voir Le Prospect Vivier',
      EDIT_VIVIER_PROSPECT: 'Éditer Le Prospect Vivier'
    };

    Object.entries(permissionsReferences).forEach(([key, value]) => {
      permissions.push({
        text: value,
        value: key
      });
    });

    const object = route.name === 'manageProfile' ? 'collaborator' : 'group';

    const recruiterPermissions = ref(store[object].permissions?.recruiter || []);

    const hasAllPermissions = computed(() => recruiterPermissions.value.length === Object.keys(permissionsReferences).length);

    const toggleRecruiterPermissions = val => {
      if (val) {
        recruiterPermissions.value = [...Object.keys(permissionsReferences)];
      } else {
        recruiterPermissions.value = [];
      }
    };

    const reset = () => {
      recruiterPermissions.value = store[object].permissions?.recruiter || [];
    };

    // Update Collaborator Permissions
    const { mutate: mutationUpdateCollaboratorPermissions, onError: errorUpdateCollaborator, onDone: doneUpdateCollaborator } = useMutation(
      getGraphqlSchema('updateCollaboratorPermissions'),
      () => ({
        variables:
          {
            uuid: store.collaborator.uuid,
            permissions: { recruiter: recruiterPermissions.value }
          }
      })
    );

    errorUpdateCollaborator(() => {
      store.loading = false;

      EventBus.$emit('show-notifications', [{
        message: getLabel('error_updating_resource'),
        type: 'danger'
      }]);
    });

    doneUpdateCollaborator(result => {
      store.collaborator = { ...store.collaborator, ...result.data.updateCollaborator };

      recruiterPermissions.value = result.data.updateCollaborator.permissions.recruiter;
      EventBus.$emit('show-notifications', [{
        message: getLabel('resource_updated'),
        type: 'success'
      }]);

      store.loading = false;
    });

    /** ******************************************* */

    // Update Group Roles
    const { mutate: mutationUpdatePermissions, onError: errorUpdateGroup, onDone: doneUpdateGroup } = useMutation(
      getGraphqlSchema('updateGroupPermissions'),
      () => ({
        variables:
          {
            uuid: store.group.uuid,
            permissions: { recruiter: recruiterPermissions.value }
          }
      })
    );

    errorUpdateGroup(() => {
      store.loading = false;
      EventBus.$emit('show-notifications', [{
        message: getLabel('error_updating_resource'),
        type: 'danger'
      }]);
    });

    doneUpdateGroup(result => {
      store.group = { ...store.group, ...result.data.updateGroup };

      recruiterPermissions.value = result.data.updateGroup.permissions.recruiter;
      EventBus.$emit('show-notifications', [{
        message: getLabel('resource_updated'),
        type: 'success'
      }]);

      store.loading = false;
    });

    /** ********************************************* */

    const savePermissions = () => {
      store.loading = true;

      EventBus.$emit('cancel-workspaces-edit');

      if (object === 'collaborator') {
        mutationUpdateCollaboratorPermissions();
      } else {
        mutationUpdatePermissions();
      }
    };

    return { getLabel, permissions, recruiterPermissions, reset, savePermissions, hasAllPermissions, toggleRecruiterPermissions };
  }

};

</script>
